import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { PersonalInfoService, DocumentService, ApplicationFormService, AppointmentService } from '@/integrations/supabase/services';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { User, FileText, Upload, Calendar, Check, X } from 'lucide-react';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { DashboardLayout } from '@/components/DashboardLayout';

const ReviewApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [applicationData, setApplicationData] = useState({
    personalInfo: null,
    documents: [],
    applicationForm: null,
    appointments: []
  });
  const { completionStatus, loading: statusLoading } = useCompletionStatus();

  useEffect(() => {
    if (user) {
      loadApplicationData();
    }
  }, [user]);

  const loadApplicationData = async () => {
    if (!user) return;

    try {
      const [personalInfoList, documents, applicationForms, appointments] = await Promise.all([
        PersonalInfoService.getByUserId(user.uid),
        DocumentService.getByUserId(user.uid),
        ApplicationFormService.getByUserId(user.uid),
        AppointmentService.getByUserId(user.uid)
      ]);

      setApplicationData({
        personalInfo: personalInfoList.length > 0 ? personalInfoList[0] : null,
        documents: documents || [],
        applicationForm: applicationForms.length > 0 ? applicationForms[0] : null,
        appointments: appointments || []
      });
    } catch (error) {
      console.error('Error loading application data:', error);
      toast({
        title: "Error loading data",
        description: "Could not load your application data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSectionStatus = (data: any) => {
    return data ? 'completed' : 'pending';
  };

  const getDocumentStatus = () => {
    return applicationData.documents.length >= 3 ? 'completed' : 'pending';
  };

  if (loading || statusLoading) {
    return (
      <DashboardLayout pageTitle="Review Application">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Review Application">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Review Your Application</CardTitle>
            <CardDescription>
              Review all the information before proceeding to schedule an appointment
            </CardDescription>
          </CardHeader>

          {/* Personal Information Section */}
          <Card className="hover:shadow-md transition-all duration-200 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-full bg-blue-50">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">Personal Information</CardTitle>
                </div>
                <Badge variant={getSectionStatus(applicationData.personalInfo) === 'completed' ? 'default' : 'secondary'}>
                  {getSectionStatus(applicationData.personalInfo) === 'completed' ? (
                    <><Check className="h-3 w-3 mr-1" /> Complete</>
                  ) : (
                    <><X className="h-3 w-3 mr-1" /> Pending</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {applicationData.personalInfo ? (
                <div className="grid gap-4">
                  <div className="space-y-4">
                    {/* Personal Info Fields */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Full Name</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.full_name}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Phone</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.phone}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Address</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.address}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-500 mb-1">Nationality</div>
                        <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.nationality}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-500 mb-1">Date of Birth</div>
                        <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.date_of_birth}</div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Passport Number</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.personalInfo.passport_number}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 mb-4">Personal information not completed yet.</p>
                  <Button 
                    onClick={() => navigate('/personal-info')} 
                    className="bg-sigma-red hover:bg-sigma-dark-red text-white transition-all duration-200"
                  >
                    Complete Personal Information
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Section */}
          <Card className="hover:shadow-md transition-all duration-200 animate-fade-in" style={{ animationDelay: '300ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-full bg-purple-50">
                    <Upload className="h-5 w-5 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">Documents</CardTitle>
                </div>
                <Badge variant={getDocumentStatus() === 'completed' ? 'default' : 'secondary'}>
                  {getDocumentStatus() === 'completed' ? (
                    <><Check className="h-3 w-3 mr-1" /> Complete</>
                  ) : (
                    <><X className="h-3 w-3 mr-1" /> Pending</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {applicationData.documents.length > 0 ? (
                <div className="space-y-3">
                  {applicationData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                      <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                        <p className="text-xs text-gray-500">{doc.document_type.replace('_', ' ').toUpperCase()}</p>
                      </div>
                      <Badge variant="outline" className="flex-shrink-0">Uploaded</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 mb-4">No documents uploaded yet.</p>
                  <Button 
                    onClick={() => navigate('/documents')} 
                    className="bg-sigma-red hover:bg-sigma-dark-red text-white transition-all duration-200"
                  >
                    Upload Documents
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Application Form Section */}
          <Card className="hover:shadow-md transition-all duration-200 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-full bg-orange-50">
                    <FileText className="h-5 w-5 text-orange-600" />
                  </div>
                  <CardTitle className="text-lg">Application Form</CardTitle>
                </div>
                <Badge variant={getSectionStatus(applicationData.applicationForm) === 'completed' ? 'default' : 'secondary'}>
                  {getSectionStatus(applicationData.applicationForm) === 'completed' ? (
                    <><Check className="h-3 w-3 mr-1" /> Complete</>
                  ) : (
                    <><X className="h-3 w-3 mr-1" /> Pending</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {applicationData.applicationForm ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Purpose of Stay</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.applicationForm.purpose_of_stay}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Employment Status</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.applicationForm.employment_status}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Monthly Income</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.applicationForm.monthly_income} PLN</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Stay Duration</div>
                      <div className="text-sm font-medium text-gray-900">{applicationData.applicationForm.planned_stay_duration} months</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 mb-4">Application form not completed yet.</p>
                  <Button 
                    onClick={() => navigate('/application-form')} 
                    className="bg-sigma-red hover:bg-sigma-dark-red text-white transition-all duration-200"
                  >
                    Complete Application Form
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4 animate-fade-in" style={{ animationDelay: '500ms' }}>
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="w-full border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white transition-all duration-200"
            >
              Back to Dashboard
            </Button>
            {applicationData.personalInfo && applicationData.documents.length >= 3 && applicationData.applicationForm && (
              <Button 
                onClick={() => navigate('/appointment')}
                className="w-full bg-sigma-red hover:bg-sigma-dark-red text-white transition-all duration-200"
              >
                Submit Application
              </Button>
            )}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ReviewApplication;
